import React, { useEffect, useState } from "react";
import { FileText, CheckCircle, ShieldCheck, FilePdf, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";

/**
 * Partnership contract — display + electronic signature flow.
 *
 * Two states:
 *  - Unsigned: full contract text + signature form (city, "Lu et approuvé"
 *    mention, accept checkbox, "Signer électroniquement" button).
 *  - Signed: badge with signed-at timestamp + version + "Télécharger PDF signé"
 *    button. The contract content stays viewable for reference.
 */
export default function TeleproContract({ onSigned }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [city, setCity] = useState("");
  const [lu, setLu] = useState("");
  const [accept, setAccept] = useState(false);
  const [acceptIndep, setAcceptIndep] = useState(false);
  const [acceptNoSalarie, setAcceptNoSalarie] = useState(false);
  const [acceptSiret, setAcceptSiret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    teleproApi.get("/me/contract")
      .then((r) => {
        setData(r.data);
        if (r.data?.parties?.partner?.city) setCity(r.data.parties.partner.city);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Chargement impossible"));
  };
  useEffect(load, []);

  const sign = async () => {
    setSubmitting(true);
    try {
      const r = await teleproApi.post("/me/contract/sign", {
        city: city.trim(),
        lu_approuve: lu.trim(),
        accept,
        accept_independent_status: acceptIndep,
        accept_no_salarie: acceptNoSalarie,
        accept_siret_commitment: acceptSiret,
      });
      toast.success("Contrat signé. Une copie t'a été envoyée par email.");
      load();
      onSigned && onSigned(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Signature impossible";
      toast.error(typeof msg === "string" ? msg : "Vérifie les champs obligatoires");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await teleproApi.get("/me/contract.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "contrat-partenariat.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible");
    } finally {
      setDownloading(false);
    }
  };

  if (error) return <p className="text-sm text-secondary">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Chargement du contrat…</p>;

  const signed = data.signature.signed;
  const p = data.parties.partner;
  const mandatory = data.mandatory_checkboxes || [];
  const isValid = city.trim().length >= 2
    && lu.trim().toLowerCase().replace("é", "e") === "lu et approuve"
    && accept && acceptIndep && acceptNoSalarie && acceptSiret;

  return (
    <div data-testid="telepro-contract">
      {/* Signed badge OR unsigned warning */}
      {signed ? (
        <div className="bg-primary/10 border-l-4 border-primary rounded-sm p-4 mb-4 flex items-start gap-3" data-testid="contract-signed-badge">
          <CheckCircle size={24} weight="fill" className="text-primary shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-primary mb-0.5">Contrat signé électroniquement</div>
            <div className="text-xs text-muted-foreground">
              Signé le <b className="text-foreground">{new Date(data.signature.signed_at).toLocaleString("fr-FR")}</b> · Version <b className="text-foreground">{data.signature.signed_version}</b>
            </div>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading}
              className="mt-3 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-xs font-bold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="contract-download-pdf"
            >
              <FilePdf size={13} weight="bold"/>
              {downloading ? "Préparation…" : "Télécharger le PDF signé"}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-secondary/10 border-l-4 border-secondary rounded-sm p-4 mb-4 flex items-start gap-3" data-testid="contract-unsigned-banner">
          <Warning size={24} weight="duotone" className="text-secondary shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-secondary mb-0.5">Contrat non signé</div>
            <p className="text-xs text-foreground/90 leading-relaxed">
              Lis le contrat ci-dessous puis signe-le électroniquement en bas pour finaliser ton partenariat.
              Une copie PDF te sera envoyée par email et téléchargeable depuis ce portail.
            </p>
          </div>
        </div>
      )}

      {/* Parties summary */}
      <div className="bg-card border border-border rounded-sm p-4 mb-4 text-xs leading-relaxed">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Digital Corp France</div>
            <div className="font-semibold">{data.parties.company.name}</div>
            <div className="text-muted-foreground">Représentée par {data.parties.company.representative}</div>
            <div className="text-muted-foreground">Plateforme : {data.parties.company.platform}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Le Partenaire</div>
            <div className="font-semibold">{p.first_name} {p.last_name} <span className="font-mono text-primary">· {p.code}</span></div>
            <div className="text-muted-foreground">{p.address}, {p.postal_code} {p.city}</div>
            <div className="text-muted-foreground">{p.phone} · {p.email}</div>
          </div>
        </div>
      </div>

      {/* Articles */}
      <div className="bg-card border border-border rounded-sm divide-y divide-border max-h-[60vh] overflow-y-auto" data-testid="contract-articles">
        {data.articles.map((a, i) => (
          <div key={i} className="p-4">
            <div className="font-display font-semibold text-primary text-sm mb-2">ARTICLE {i + 1} — {a.title}</div>
            <div className="text-xs leading-relaxed text-foreground/90 whitespace-pre-line"
                 dangerouslySetInnerHTML={{ __html: a.body }}/>
          </div>
        ))}
      </div>

      {/* Signature form OR signed mention */}
      {!signed ? (
        <div className="mt-4 bg-card border-2 border-primary/40 rounded-sm p-4" data-testid="contract-sign-form">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} weight="duotone" className="text-primary"/>
            <span className="font-display font-semibold">Signature électronique</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Conformément à l&apos;Article 17, ta signature électronique a la même valeur qu&apos;une signature manuscrite.
            Horodatage, IP et navigateur sont enregistrés pour preuve juridique.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <label className="block text-xs">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Fait à</span>
              <input
                type="text" value={city} onChange={(e) => setCity(e.target.value.slice(0, 80))}
                placeholder="Ville de signature"
                className="w-full border border-border rounded-sm px-2.5 py-2 bg-background focus:border-primary outline-none"
                data-testid="contract-city"
              />
            </label>
            <label className="block text-xs">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Mention manuscrite</span>
              <input
                type="text" value={lu} onChange={(e) => setLu(e.target.value.slice(0, 120))}
                placeholder="Tape exactement : Lu et approuvé"
                className="w-full border border-border rounded-sm px-2.5 py-2 bg-background focus:border-primary outline-none font-mono"
                data-testid="contract-lu-approuve"
              />
            </label>
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-0.5"
              data-testid="contract-accept"
            />
            <span>
              Je reconnais avoir <b>lu, compris et accepté</b> l&apos;intégralité des {data.articles?.length || 15} articles
              du présent contrat, et je m&apos;engage à respecter les règles de prospection et d&apos;opposition
              (Article 5) et de confidentialité (Article 12).
            </span>
          </label>

          {/* Cases à cocher OBLIGATOIRES — cahier de modifs 2026-02 */}
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 mb-3 space-y-2" data-testid="contract-mandatory-checkboxes">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
              Engagements obligatoires
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={acceptIndep}
                onChange={(e) => setAcceptIndep(e.target.checked)}
                className="mt-0.5"
                data-testid="contract-accept-independent"
              />
              <span>{mandatory[0] || "Je certifie exercer sous un statut professionnel indépendant régulier."}</span>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={acceptNoSalarie}
                onChange={(e) => setAcceptNoSalarie(e.target.checked)}
                className="mt-0.5"
                data-testid="contract-accept-no-salarie"
              />
              <span>{mandatory[1] || "Je certifie ne pas être salarié(e) de Digital Corp France."}</span>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={acceptSiret}
                onChange={(e) => setAcceptSiret(e.target.checked)}
                className="mt-0.5"
                data-testid="contract-accept-siret"
              />
              <span>{mandatory[2] || "Je m'engage à communiquer mon SIREN/SIRET dès son attribution."}</span>
            </label>
          </div>
          <button
            type="button"
            onClick={sign}
            disabled={!isValid || submitting}
            className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contract-sign-btn"
          >
            <ShieldCheck size={14} weight="bold"/>
            {submitting ? "Signature en cours…" : "Signer électroniquement"}
          </button>
        </div>
      ) : (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading}
            className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="contract-download-pdf-bottom"
          >
            <FilePdf size={14} weight="bold"/>
            {downloading ? "Préparation…" : "Télécharger ma copie PDF signée"}
          </button>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-3 text-center flex items-center justify-center gap-1">
        <FileText size={10} weight="duotone"/> Contrat v{data.version} · Paiement le {data.payment_day} du mois · {data.payment_method}
      </p>
    </div>
  );
}
